import React from 'react';
import { formatCurrency } from 'utils';
import { Link, Save, Score } from '@mui/icons-material';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

export interface RevenueProfitCardProps {
  finance: number;
  equipment: number;
  aftermarket: number;
  handleRedirectToDashboard: VoidFunction;
  loading: { isProposalRevenueDataLoading: boolean; isEquipmentUpdating: boolean };
  onUnifiedSave?: VoidFunction;
  isSaveDisabled?: boolean;
  isSaving?: boolean;
}

export const RevenueCard = ({
  finance,
  loading,
  equipment,
  aftermarket,
  handleRedirectToDashboard,
  onUnifiedSave,
  isSaveDisabled = true,
  isSaving = false,
}: RevenueProfitCardProps) => {
  return (
    <Stack sx={{ height: 'fit-content' }} className="revenueCard">
      <Box className="revenueCardHeader">
        <Box className="revenueCardHeaderIconWithTitle">
          <Box className="revenueCardHeaderIcon">
            <Score />
          </Box>

          <Typography variant="subtitle1" component="span">
            Profit
          </Typography>
        </Box>

        <Stack direction="row" gap="0.5rem">
          {onUnifiedSave && (
            <Button
              variant="contained"
              startIcon={<Save />}
              disabled={isSaveDisabled}
              loading={isSaving}
              onClick={onUnifiedSave}
            >
              Save
            </Button>
          )}
          <Button startIcon={<Link />} variant="contained" onClick={handleRedirectToDashboard}>
            Dashboard
          </Button>
        </Stack>
      </Box>

      <Box className="revenueProfitContent">
        <Box className="revenueTotalProfit">
          <Stack direction="row" alignItems="center" gap="0.5rem">
            <Typography variant="h6" component="span">
              Total ($):
            </Typography>
            <Typography variant="h6">{formatCurrency(finance + equipment + aftermarket, true)}</Typography>
            <RenderWithCondition condition={loading.isProposalRevenueDataLoading || loading.isEquipmentUpdating}>
              <CircularProgress size={20} />
            </RenderWithCondition>
          </Stack>
          <Typography color="textDisabled">Finance + Aftermarket + Equipment</Typography>
        </Box>

        <Box className="revenueListProfit">
          <Box className="revenueListItemProfit">
            <Typography>Finance:</Typography>

            <Typography>{formatCurrency(finance, true)}</Typography>
          </Box>
          <Box className="revenueListItemProfit">
            <Typography>Aftermarket:</Typography>

            <RenderWithFallback
              isLoading={loading.isProposalRevenueDataLoading}
              fallBack={<Typography>Calculating...</Typography>}
            >
              <Typography>{formatCurrency(aftermarket, true)}</Typography>
            </RenderWithFallback>
          </Box>
          <Box className="revenueListItemProfit">
            <Typography>Equipment:</Typography>

            <RenderWithFallback
              isLoading={loading.isEquipmentUpdating}
              fallBack={<Typography>Calculating...</Typography>}
            >
              <Typography>{formatCurrency(equipment, true)}</Typography>
            </RenderWithFallback>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};
